import React from 'react';
import { Search, Calendar, Check, XCircle, Grid, List, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { t } from 'i18next';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import CompactDatePicker from '../ui/compact-date-picker';

type DateRangeMode = 'day' | 'week' | 'month';
type ViewMode = 'list' | 'grid';

interface ReservationFiltersProps {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dateRangeMode: DateRangeMode;
  selectedDate: Date;
  setDateRangeMode: (mode: DateRangeMode) => void;
  setSelectedDate: (date: Date) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  onExportClick?: () => void;
}

const ReservationFilters: React.FC<ReservationFiltersProps> = ({
  activeFilter,
  setActiveFilter,
  searchTerm,
  setSearchTerm,
  dateRangeMode,
  selectedDate,
  setDateRangeMode,
  setSelectedDate,
  viewMode,
  setViewMode,
  onExportClick
}) => {
  // Naviguer vers la période précédente
  const handlePrevious = () => {
    switch (dateRangeMode) {
      case 'day':
        setSelectedDate(addDays(selectedDate, -1));
        break;
      case 'week':
        setSelectedDate(addWeeks(selectedDate, -1));
        break;
      case 'month':
        setSelectedDate(addMonths(selectedDate, -1));
        break;
    }
  };

  // Naviguer vers la période suivante
  const handleNext = () => {
    switch (dateRangeMode) {
      case 'day':
        setSelectedDate(addDays(selectedDate, 1));
        break;
      case 'week':
        setSelectedDate(addWeeks(selectedDate, 1));
        break;
      case 'month':
        setSelectedDate(addMonths(selectedDate, 1));
        break;
    }
  };

  // Gérer le changement de mode
  const handleModeChange = (mode: DateRangeMode) => {
    setDateRangeMode(mode);
  };

  // Gérer la sélection d'une date dans le picker
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2 reservation-filter-status">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'all'
                ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('all')}
          >
            {t('reservations.all', 'Toutes')}
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${
              activeFilter === 'confirmed'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('confirmed')}
          >
            <Check size={14} className="mr-1" />
            {t('reservations.confirmed', 'Confirmées')}
          </button>
          <button
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${
              activeFilter === 'canceled'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            onClick={() => setActiveFilter('cancelled')}
          >
            <XCircle size={14} className="mr-1" />
            {t('reservations.cancelled', 'Annulées')}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid'
                ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Grid size={18} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 reservation-filter-week">
          <Calendar size={16} className="text-gray-500" />

          {/* Sélecteur de mode (Jour/Semaine/Mois) */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleModeChange('day')}
              className={`px-3 py-1.5 text-sm ${
                dateRangeMode === 'day' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('reservations.day', 'Jour')}
            </button>
            <button
              onClick={() => handleModeChange('week')}
              className={`px-3 py-1.5 text-sm ${
                dateRangeMode === 'week' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('reservations.week', 'Semaine')}
            </button>
            <button
              onClick={() => handleModeChange('month')}
              className={`px-3 py-1.5 text-sm ${
                dateRangeMode === 'month' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('reservations.month', '1 mois')}
            </button>
          </div>

          {/* Affichage de la date et navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="min-w-[250px]">
              <CompactDatePicker
                name="filter-date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                onChange={(e) => handleDateSelect(new Date(e.target.value))}
              />
            </div>

            <button
              onClick={handleNext}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2 flex-1 sm:flex-none">
          <div className="relative flex-1 sm:w-64">
            <input
              type="text"
              placeholder={t('reservations.search', 'Rechercher...') || 'Rechercher...'}
              className="w-full py-2 pl-10 pr-4 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-primary dark:focus:border-primary-light bg-gray-50 dark:bg-gray-700"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <Search size={16} className="text-gray-400" />
            </div>
          </div>

          {/* Bouton d'export */}
          {onExportClick && (
            <button
              onClick={onExportClick}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
              title={t('reservations.export.button', 'Exporter le CA') || 'Exporter le CA'}
            >
              <Download size={16} />
              <span className="hidden sm:inline">
                {t('reservations.export.button', 'Exporter')}
              </span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ReservationFilters;
